import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { KeyRound, Package, Lightbulb, Lock, Rocket, AlertTriangle, Sparkles } from 'lucide-react';
import api from '../services/api';
import DjamannLoader from './DjamannLoader';
import logger from '../utils/logger';

/**
 * RouteGuard - Protection automatique des routes basée sur les modules d'abonnement
 * 
 * Vérifie si la route actuelle correspond à un pattern de module protégé.
 * Si oui, vérifie si l'utilisateur a accès via son abonnement.
 * Sinon, affiche la page d'accès refusé avec les packs disponibles.
 */
const RouteGuard = ({ children }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(true);
  const [requiredModule, setRequiredModule] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [userAccess, setUserAccess] = useState(null);

  const checkRouteAccess = useCallback(async () => {
    setLoading(true);
    
    try {
      // Récupérer tous les modules avec leurs route_patterns
      const modulesRes = await api.get('/subscriptions/modules');
      const modules = modulesRes.data || [];
      
      // Vérifier si la route actuelle match un pattern protégé
      const currentPath = location.pathname;
      let matchedModule = null;
      
      for (const mod of modules) {
        if (mod.route_pattern && mod.is_active) {
          // Convertir le pattern en regex
          // /business/* → matche /business, /business/, /business/xxx, /business/xxx/yyy
          let pattern = mod.route_pattern;
          
          // Retirer le * à la fin pour traiter séparément
          if (pattern.endsWith('/*')) {
            const basePath = pattern.slice(0, -2); // enlever /*
            const escapedBase = basePath.replace(/\//g, '\\/');
            // Matcher la racine OU la racine avec sous-chemins
            pattern = `^${escapedBase}(\\/.*)?$`;
          } else if (pattern.endsWith('*')) {
            const basePath = pattern.slice(0, -1); // enlever *
            const escapedBase = basePath.replace(/\//g, '\\/');
            pattern = `^${escapedBase}.*$`;
          } else {
            // Pattern exact
            const escapedPattern = pattern.replace(/\//g, '\\/');
            pattern = `^${escapedPattern}$`;
          }
          
          const regex = new RegExp(pattern);
          
          if (regex.test(currentPath)) {
            matchedModule = mod;
            break;
          }
        }
      }
      
      // Si aucun module ne protège cette route, accès libre
      if (!matchedModule) {
        setAccessGranted(true);
        setLoading(false);
        return;
      }
      
      // Si pas connecté, bloquer l'accès
      if (!user) {
        setAccessGranted(false);
        setRequiredModule(matchedModule);
        await loadAvailablePlans(matchedModule.code);
        setLoading(false);
        return;
      }
      
      // Vérifier si l'utilisateur a accès à ce module
      const accessRes = await api.get('/subscriptions/my');
      const access = accessRes.data.access || {};
      setUserAccess(access);
      
      if (access[matchedModule.code]) {
        setAccessGranted(true);
      } else {
        setAccessGranted(false);
        setRequiredModule(matchedModule);
        await loadAvailablePlans(matchedModule.code);
      }
    } catch (err) {
      logger.error('Erreur vérification accès:', err);
      // En cas d'erreur, laisser passer pour ne pas bloquer
      setAccessGranted(true);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, user]);

  useEffect(() => {
    checkRouteAccess();
  }, [checkRouteAccess]);

  const loadAvailablePlans = async (moduleCode) => {
    try {
      const plansRes = await api.get('/subscriptions/plans');
      // Filtrer les packs qui contiennent ce module et trier par display_order puis par prix
      const plansWithModule = plansRes.data
        .filter(plan => plan.modules && plan.modules.includes(moduleCode))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || (a.prix_mensuel - b.prix_mensuel));
      setAvailablePlans(plansWithModule);
    } catch (err) {
      logger.error('Erreur chargement packs:', err);
    }
  };

  // Affichage pendant le chargement
  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  // Accès refusé - Afficher la page d'abonnement requis
  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Card principale */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative">
                <span className="text-7xl mb-4 block animate-bounce"><KeyRound className="w-16 h-16 mx-auto" /></span>
                <h1 className="text-3xl md:text-4xl font-bold mb-3">Accès Restreint</h1>
                <p className="text-orange-100 text-lg">
                  Oups ! Cette fonctionnalité nécessite un abonnement premium
                </p>
              </div>
            </div>
            
            {/* Contenu */}
            <div className="p-6 md:p-8">
              {/* Module requis */}
              {requiredModule && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5 mb-6 border-2 border-purple-100">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md">
                      <span className="text-4xl">{requiredModule.icone || <Package className="w-10 h-10 text-purple-600" />}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-purple-600 font-semibold uppercase tracking-wide mb-1">Module requis</div>
                      <div className="text-xl font-bold text-gray-900">{requiredModule.nom}</div>
                      <div className="text-sm text-gray-600 mt-1">{requiredModule.description}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Message selon si connecté ou non */}
              {!user ? (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-yellow-800 mb-1">Connexion requise</div>
                      <p className="text-yellow-700 text-sm">
                        Connectez-vous pour souscrire à un abonnement et débloquer cette fonctionnalité.
                      </p>
                      <Link
                        to="/login"
                        className="inline-block mt-3 px-5 py-2 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors text-sm"
                      >
                        Se connecter
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-blue-800 mb-1">
                        {userAccess?.has_subscription 
                          ? "Upgrade nécessaire"
                          : "Aucun abonnement actif"
                        }
                      </div>
                      <p className="text-blue-700 text-sm">
                        {userAccess?.has_subscription 
                          ? `Votre pack actuel (${userAccess?.plan_nom || 'Basique'}) ne donne pas accès à cette fonctionnalité. Passez à un pack supérieur pour en profiter !`
                          : "Souscrivez à un abonnement pour débloquer toutes les fonctionnalités et développer votre activité."
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Packs disponibles */}
              {availablePlans.length > 0 && (
                <>
                  <div className="text-center mb-4">
                    <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
                      <Sparkles className="w-4 h-4 inline mr-1" /> {availablePlans.length === 1 
                        ? `1 pack donne accès à "${requiredModule?.nom}"` 
                        : `${availablePlans.length} packs donnent accès à "${requiredModule?.nom}"`}
                    </span>
                  </div>
                  
                  <div className="grid gap-3">
                    {availablePlans.map((plan, index) => (
                      <div 
                        key={plan.id}
                        className={`border-2 rounded-2xl p-4 flex items-center justify-between hover:shadow-lg transition-all cursor-pointer ${
                          index === 0 
                            ? 'border-purple-300 bg-purple-50/50 ring-2 ring-purple-200' 
                            : 'border-gray-100 hover:border-purple-300'
                        }`}
                        onClick={() => window.location.href = '/my-subscription'}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: plan.couleur + '20' }}>
                            <span className="text-2xl">{plan.icone}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-gray-900">{plan.nom}</span>
                              {index === 0 && (
                                <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded-full">Recommandé</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {plan.module_names?.length || 0} module{(plan.module_names?.length || 0) > 1 ? 's' : ''} inclus
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {plan.module_names?.slice(0, 4).join(' • ')}
                              {plan.module_names?.length > 4 && ` +${plan.module_names.length - 4}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold" style={{ color: plan.couleur }}>
                            {Number(plan.prix_mensuel).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-400">XOF/mois</div>
                          <div className="text-xs text-green-600 mt-1">
                            À partir de {Number(plan.prix_mensuel).toLocaleString()} XOF
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {/* Message si aucun pack ne contient ce module */}
              {availablePlans.length === 0 && requiredModule && (
                <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-5 mb-6 text-center">
                  <span className="text-4xl mb-3 block"><Lock className="w-10 h-10 mx-auto text-gray-600" /></span>
                  <div className="font-semibold text-gray-700">
                    Le module "{requiredModule.nom}" n'est disponible dans aucun pack actuellement
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Contactez l'administrateur pour plus d'informations.
                  </p>
                </div>
              )}
              
              {/* Bouton principal */}
              <div className="mt-8">
                <Link
                  to="/my-subscription"
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Rocket className="w-5 h-5" />
                  <span>Voir les packs d'abonnement</span>
                </Link>
              </div>
              
              {/* Bouton retour */}
              <div className="mt-4 text-center">
                <button
                  onClick={() => window.history.back()}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-sm"
                >
                  ← Retourner à la page précédente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Accès autorisé - Afficher le contenu
  return children;
};

export default RouteGuard;
